import React, { createContext, useState, useContext, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Theme colors
export const themes = {
  light: {
    // Background colors
    bgPrimary: '#ffffff',
    bgSecondary: '#f8f9fa',
    bgTertiary: '#f1f5f9',
    bgCard: '#ffffff',
    bgHover: '#f1f5f9',
    bgActive: '#e0e7ff',

    // Border colors
    borderColor: '#e5e7eb',
    borderPrimary: '#e5e7eb',
    borderSecondary: '#d1d5db',
    borderHover: '#cbd5e1',

    // Text colors
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    textTertiary: '#94a3b8',
    textActive: '#3b82f6',
    textError: '#ef4444',

    // Button colors
    buttonPrimary: '#2a2a2a',
    buttonPrimaryText: '#ffffff',
    buttonSecondary: 'transparent',
    buttonSecondaryText: '#64748b',

    // Input colors
    inputBg: '#f8f9fa',
    inputBorder: '#e5e7eb',
    inputText: '#1e293b',
    inputPlaceholder: '#94a3b8'
  },
  dark: {
    // Background colors
    bgPrimary: '#1a1a1a',
    bgSecondary: '#2a2a2a',
    bgTertiary: '#3a3a3a',
    bgCard: '#2a2a2a',
    bgHover: '#3a3a3a',
    bgActive: '#2a2a2a',

    // Border colors
    borderColor: '#2a2a2a',
    borderPrimary: '#2a2a2a',
    borderSecondary: '#3a3a3a',
    borderHover: '#4a4a4a',

    // Text colors
    textPrimary: '#ffffff',
    textSecondary: '#94a3b8',
    textTertiary: '#64748b',
    textActive: '#ffffff',
    textError: '#ef4444',

    // Button colors
    buttonPrimary: '#ffffff',
    buttonPrimaryText: '#1a1a1a',
    buttonSecondary: 'transparent',
    buttonSecondaryText: '#94a3b8',

    // Input colors
    inputBg: '#2f2f2f',
    inputBorder: '#3f3f3f',
    inputText: '#ffffff',
    inputPlaceholder: '#a0a0a0'
  }
};

// Convert theme object to CSS variables and apply to :root
const applyThemeToCSS = (themeColors) => {
  const root = document.documentElement;

  // Map theme keys to CSS variable names
  const cssVarMap = {
    bgPrimary: '--bg-primary',
    bgSecondary: '--bg-secondary',
    bgTertiary: '--bg-tertiary',
    bgCard: '--bg-card',
    bgHover: '--bg-hover',
    bgActive: '--bg-active',
    borderColor: '--border-color',
    borderPrimary: '--border-primary',
    borderSecondary: '--border-secondary',
    borderHover: '--border-hover',
    textPrimary: '--text-primary',
    textSecondary: '--text-secondary',
    textTertiary: '--text-tertiary',
    textActive: '--text-active',
    textError: '--text-error',
    buttonPrimary: '--button-primary',
    buttonPrimaryText: '--button-primary-text',
    buttonSecondary: '--button-secondary',
    buttonSecondaryText: '--button-secondary-text',
    inputBg: '--input-bg',
    inputBorder: '--input-border',
    inputText: '--input-text',
    inputPlaceholder: '--input-placeholder',
  };

  Object.entries(themeColors).forEach(([key, value]) => {
    const cssVar = cssVarMap[key];
    if (cssVar) {
      root.style.setProperty(cssVar, value);
    }
  });
};

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState('light');

  const theme = themes[themeMode];

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    applyThemeToCSS(theme);
  }, [theme]);

  const toggleTheme = () => {
    setThemeMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  const value = {
    themeMode,
    theme,
    toggleTheme,
    setThemeMode
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
